/**
 * The crate gesture of the end-of-line bench: open a box, pour crates into it,
 * close it, and read the packing list of the order being packed.
 */

import type { ReactNode } from 'react';
import type { CarrierState, Consignee } from '@errebi/domain';

import { Sezione } from '../../components/section';
import { PesoAMano } from './manual-weight';

export interface Lavoro {
  ordine: string;
  cliente: string;
  consegna: string;
  destinatario: Consignee | null;
  urgente: boolean;
  ritiro: boolean;
  deroga: { chi: string; manca: string } | null;
  carrelli: string[];
  capi: number;
  cassette: string[];
  // The weighing off the PS130, by the service, for the parcel currently
  // open on this order. `null` means nobody has beeped it yet — not zero.
  misura: { pesoKg: number | null; volumeCm3: number | null; quando: string } | null;
}
export interface Collo {
  barcode: string;
  progressivo: number;
  chiuso: boolean;
  pesoKg: number | null;
  contenuto: { cassetta: string; capi: number }[];
}
export interface Spedizione {
  ordine: string;
  colli: Collo[];
  chiusa: boolean;
  corriere: string | null;
  ragioneCorriere: string | null;
  // Absent on shipments that never went to a carrier (pick-up, or written before).
  vettore?: CarrierState | null;
}

export type Cassette = Record<
  string,
  { codice: string; capi: number; rovinati: number; imballata: boolean }
>;

function cassetteFuori(n: number): string {
  return n === 1
    ? '1 cassetta ancora fuori dalle scatole'
    : `${n} cassette ancora fuori dalle scatole`;
}

export function OrdineCassette({
  lavoro,
  sped,
  cassette,
  inCorso,
  fai,
  azioni,
}: {
  lavoro: Lavoro;
  sped: Spedizione | undefined;
  cassette: Cassette;
  inCorso: boolean;
  fai: (percorso: string, corpo?: unknown) => () => void;
  azioni?: ReactNode;
}) {
  const aperto = sped?.colli.find((c) => !c.chiuso);
  const fuori = lavoro.cassette;

  return (
    <Sezione
      titolo={
        <>
          {lavoro.cliente} · ordine {lavoro.ordine} {lavoro.urgente && '· urgente'}
        </>
      }
      azioni={azioni}
    >
      {lavoro.deroga && (
        <p className="avviso avviso--no" style={{ margin: '0 0 12px' }}>
          <b>Parziale autorizzato da {lavoro.deroga.chi}</b>
          <br />
          manca {lavoro.deroga.manca} — leggilo <b>prima</b> di chiudere il collo.
        </p>
      )}

      <div className="gesto">
        <div className="gesto__top">
          {aperto ? (
            <>
              <span className="gesto__cass">Scatola {aperto.progressivo}</span>
              <span className="gesto__fill">
                {aperto.contenuto.reduce((n, x) => n + x.capi, 0)} capi
              </span>
            </>
          ) : (
            <span className="gesto__k">Nessuna scatola aperta</span>
          )}
        </div>
        <div className="gesto__body">
          {!aperto && fuori.length === 0 ? (
            // Out of the ready list nothing is left to pour: an empty box could
            // never be closed, and would hold the shipment back.
            <p className="mod__hint" style={{ margin: 0 }}>
              Tutte le cassette sono nelle scatole.
            </p>
          ) : !aperto ? (
            <div className="mods">
              <button
                className="btn btn--primario"
                disabled={inCorso}
                onClick={fai(`/fine-linea/${lavoro.ordine}/collo`)}
              >
                Aggiungi scatola
              </button>
              <span className="mod__hint">
                Quante ne servano lo decidi tu: nessuna capienza a codice.
              </span>
            </div>
          ) : (
            <>
              <div className="cassette">
                {fuori.map((cod) => (
                  <button
                    key={cod}
                    className="cassetta"
                    disabled={inCorso}
                    onClick={fai('/fine-linea/versa', { cassetta: cod })}
                  >
                    <span className="cassetta__cod">{cod}</span>
                    <span className="cassetta__n">
                      versa {(cassette[cod]?.capi ?? 0) - (cassette[cod]?.rovinati ?? 0)} capi
                    </span>
                  </button>
                ))}
              </div>
              <div className="mods mods--stampa">
                <button
                  className="btn"
                  disabled={inCorso || aperto.contenuto.length === 0}
                  onClick={fai(`/fine-linea/${lavoro.ordine}/chiudi-collo`)}
                >
                  Chiudi collo
                </button>
                <span className="mod__hint">
                  {/*
                            A measurement can arrive with a volume and no weight — the PS130
                            sends it that way when the frame carries one without the other.
                            Without a weight there is nothing a courier can be chosen on, so
                            it counts the same as no measurement at all: the operator still
                            gets the prompt to beep, never a stray "undefined kg".
                          */}
                  {lavoro.misura && lavoro.misura.pesoKg !== null
                    ? `${lavoro.misura.pesoKg.toLocaleString('it-IT', { maximumFractionDigits: 1 })} kg misurati sul PS130.`
                    : `Bippa ${aperto.barcode} sul PS130: la misurazione parte da sola.`}
                </span>
              </div>
            </>
          )}

          {/* Everything but a pick-up goes to the carrier on its own, once every
                      box is closed and weighed: the manual close stays only for
                      the customer who comes to collect. */}
          {lavoro.ritiro ? (
            <div className="mods mods--stampa">
              <button
                className="btn btn--primario"
                disabled={
                  inCorso || !sped || sped.colli.length === 0 || !!aperto || fuori.length > 0
                }
                onClick={fai(`/fine-linea/${lavoro.ordine}/spedisci`)}
              >
                Chiudi spedizione
              </button>
              <span className="mod__hint">
                {fuori.length > 0
                  ? `${cassetteFuori(fuori.length)}.`
                  : 'Ritiro in sede: niente corriere.'}
              </span>
            </div>
          ) : (
            <p className="mod__hint" style={{ margin: '10px 0 0' }}>
              {/* The crates still out are why the shipment has not started yet: say so. */}
              {fuori.length > 0
                ? `${cassetteFuori(fuori.length)}: la spedizione parte quando sono tutte dentro, chiuse e pesate.`
                : 'La spedizione parte da sola quando tutte le scatole sono chiuse e pesate.'}
            </p>
          )}
        </div>
      </div>

      {sped && sped.colli.length > 0 && (
        <div style={{ marginTop: 14 }}>
          <span className="gesto__k">Packing list</span>
          {sped.colli.map((c) => (
            <article key={c.barcode} className="riga riga--ok" style={{ marginTop: 8 }}>
              <div className="riga__cliente">
                Collo {c.progressivo} {c.chiuso ? '· chiuso' : '· aperto'}
              </div>
              <div className="riga__sku">
                {c.barcode}
                {c.pesoKg ? ` · ${c.pesoKg} kg` : ''}
              </div>
              <div className="riga__conto">
                {c.contenuto.map((x) => `${x.cassetta} ×${x.capi}`).join(' · ') || 'vuoto'}
              </div>
              {c.chiuso && c.pesoKg === null && (
                <PesoAMano
                  ordine={lavoro.ordine}
                  progressivo={c.progressivo}
                  barcode={c.barcode}
                  inCorso={inCorso}
                  fai={fai}
                />
              )}
            </article>
          ))}
        </div>
      )}
    </Sezione>
  );
}
